import { useState, useEffect, useCallback } from 'react'
import {
  listSupportCannedReplies,
  upsertSupportCannedReply,
  deleteSupportCannedReply,
  listSupportMacros,
  upsertSupportMacro,
} from '../../../services/supportTicketsService'
import type {
  SupportCannedReply,
  SupportMacro,
  SupportMacroStep,
} from '../../../types/supportTicket'
import { OwnerEmptyScaffold } from '../kit/OwnerEmptyScaffold'
import './ownerSupport.css'

const SAMPLE_CONTEXT = {
  'workspace.name': 'Acme Production',
  'workspace.plan': 'Enterprise',
  'reporter.email': 'jane.doe@example.com',
  'reporter.displayName': 'Jane Doe',
  'ticket.subject': 'Sync latency on main timeline',
  'ticket.id': 'tk-98741a',
}

function interpolateSample(body: string): string {
  let result = body
  Object.entries(SAMPLE_CONTEXT).forEach(([key, val]) => {
    result = result.replaceAll(`{{${key}}}`, val)
  })
  return result
}

/* ==========================================================================
   Canned Replies Manager
   ========================================================================== */

export const OwnerCannedRepliesManager = () => {
  const [replies, setReplies] = useState<SupportCannedReply[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingReply, setEditingReply] = useState<Partial<SupportCannedReply> | null>(null)
  const [saving, setSaving] = useState(false)

  const loadReplies = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await listSupportCannedReplies()
      if (res && res.cannedReplies) {
        setReplies(res.cannedReplies)
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load canned replies.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    void listSupportCannedReplies()
      .then((res) => {
        if (!cancelled && res?.cannedReplies) {
          setReplies(res.cannedReplies)
          setLoading(false)
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load canned replies.')
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleSave = async () => {
    if (!editingReply?.name?.trim() || !editingReply?.body?.trim()) {
      alert('Name and body are required.')
      return
    }
    setSaving(true)
    try {
      const id = editingReply.id || `cr_${Date.now()}`
      await upsertSupportCannedReply({
        id,
        name: editingReply.name.trim(),
        language: editingReply.language || 'en',
        body: editingReply.body.trim(),
        tagAutoApply: editingReply.tagAutoApply || [],
      })
      setEditingReply(null)
      await loadReplies()
    } catch (err: unknown) {
      alert(`Failed to save: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete canned reply "${name}"?`)) return
    try {
      await deleteSupportCannedReply({ id })
      await loadReplies()
    } catch (err: unknown) {
      alert(`Failed to delete: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, color: 'var(--owner-text-primary, #f1f5f9)' }}>
            Canned Replies
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--owner-text-tertiary, #64748b)' }}>
            Reusable response templates with dynamic variable interpolation.
          </p>
        </div>
        <button
          type="button"
          className="ownerSupportBulkBtn"
          onClick={() =>
            setEditingReply({
              id: '',
              name: '',
              language: 'en',
              body: '',
              tagAutoApply: [],
            })
          }
        >
          + New canned reply
        </button>
      </div>

      {loading ? (
        <p style={{ color: 'var(--owner-text-secondary, #94a3b8)' }}>Loading canned replies…</p>
      ) : error ? (
        <p style={{ color: '#ef4444' }}>{error}</p>
      ) : replies.length === 0 ? (
        <OwnerEmptyScaffold
          title="No canned replies yet"
          body="Create your first canned reply template to accelerate support triage."
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
          {replies.map((reply) => (
            <div
              key={reply.id}
              className="ownerSupportSidebarCard"
              style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <strong style={{ fontSize: 14, color: 'var(--owner-text-primary, #f1f5f9)' }}>
                  {reply.name}
                </strong>
                <span
                  style={{
                    fontSize: 11,
                    background: 'var(--owner-bg-hover, #1e293b)',
                    padding: '2px 6px',
                    borderRadius: 4,
                  }}
                >
                  {reply.language || 'en'}
                </span>
              </div>
              <p
                style={{
                  fontSize: 12,
                  color: 'var(--owner-text-secondary, #94a3b8)',
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  maxHeight: 70,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {reply.body}
              </p>
              {reply.tagAutoApply && reply.tagAutoApply.length > 0 && (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {reply.tagAutoApply.map((t) => (
                    <span key={t} className="ownerSupportTagChip">
                      {t}
                    </span>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button
                  type="button"
                  className="ownerSupportHeaderActionBtn"
                  onClick={() => setEditingReply(reply)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="ownerSupportHeaderActionBtn"
                  style={{ color: '#ef4444' }}
                  onClick={() => handleDelete(reply.id, reply.name)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Canned Reply Editor Modal */}
      {editingReply && (
        <div className="ownerModalOverlay" onClick={() => setEditingReply(null)}>
          <div
            className="ownerModalDialog"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 580, width: '100%' }}
          >
            <div className="ownerModalHeader">
              <h3>{editingReply.id ? 'Edit Canned Reply' : 'New Canned Reply'}</h3>
            </div>
            <div className="ownerModalBody" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--owner-text-tertiary, #64748b)' }}>Title</label>
                <input
                  type="text"
                  className="ownerSupportTagInput"
                  placeholder="e.g. Account Verification Request"
                  value={editingReply.name || ''}
                  onChange={(e) => setEditingReply((r) => ({ ...r, name: e.target.value }))}
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ fontSize: 12, color: 'var(--owner-text-tertiary, #64748b)' }}>Language</label>
                <input
                  type="text"
                  className="ownerSupportTagInput"
                  placeholder="en"
                  value={editingReply.language || 'en'}
                  onChange={(e) => setEditingReply((r) => ({ ...r, language: e.target.value }))}
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ fontSize: 12, color: 'var(--owner-text-tertiary, #64748b)' }}>
                  Auto-apply tags (comma separated)
                </label>
                <input
                  type="text"
                  className="ownerSupportTagInput"
                  placeholder="e.g. billing, refunded"
                  value={editingReply.tagAutoApply?.join(', ') || ''}
                  onChange={(e) =>
                    setEditingReply((r) => ({
                      ...r,
                      tagAutoApply: e.target.value
                        .split(',')
                        .map((s) => s.trim())
                        .filter(Boolean),
                    }))
                  }
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <label style={{ fontSize: 12, color: 'var(--owner-text-tertiary, #64748b)' }}>Template Body</label>
                  <span style={{ fontSize: 11, color: 'var(--owner-text-tertiary, #64748b)' }}>
                    Markdown supported
                  </span>
                </div>
                <textarea
                  className="ownerSupportTextarea"
                  rows={5}
                  placeholder="Hi {{reporter.displayName}}, thanks for reaching out..."
                  value={editingReply.body || ''}
                  onChange={(e) => setEditingReply((r) => ({ ...r, body: e.target.value }))}
                  style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' }}
                />
              </div>

              {/* Variables Documentation */}
              <div
                style={{
                  background: 'var(--owner-bg-hover, #1e293b)',
                  borderRadius: 6,
                  padding: 10,
                  fontSize: 11,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                <strong>Available dynamic variables:</strong>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 4 }}>
                  <code>&#123;&#123;workspace.name&#125;&#125;</code>
                  <code>&#123;&#123;workspace.plan&#125;&#125;</code>
                  <code>&#123;&#123;reporter.displayName&#125;&#125;</code>
                  <code>&#123;&#123;reporter.email&#125;&#125;</code>
                  <code>&#123;&#123;ticket.subject&#125;&#125;</code>
                  <code>&#123;&#123;ticket.id&#125;&#125;</code>
                </div>
              </div>

              {/* Live Preview */}
              {editingReply.body && (
                <div>
                  <label style={{ fontSize: 12, color: 'var(--owner-text-tertiary, #64748b)' }}>
                    Live Preview (Sample Ticket Context)
                  </label>
                  <div
                    style={{
                      background: 'var(--owner-bg-surface, #141824)',
                      border: '1px solid var(--owner-border-subtle, #1e293b)',
                      borderRadius: 6,
                      padding: 10,
                      fontSize: 12,
                      whiteSpace: 'pre-wrap',
                      color: 'var(--owner-text-primary, #f1f5f9)',
                    }}
                  >
                    {interpolateSample(editingReply.body)}
                  </div>
                </div>
              )}
            </div>
            <div className="ownerModalActions">
              <button
                type="button"
                className="ownerModalBtn ownerModalBtn--default"
                onClick={() => setEditingReply(null)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ownerModalBtn ownerModalBtn--primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save Canned Reply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* ==========================================================================
   Macros Manager
   ========================================================================== */

export const OwnerMacrosManager = () => {
  const [macros, setMacros] = useState<SupportMacro[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingMacro, setEditingMacro] = useState<Partial<SupportMacro> | null>(null)
  const [saving, setSaving] = useState(false)

  // Step builder draft
  const [newStepKind, setNewStepKind] = useState<SupportMacroStep['kind']>('apply-tag')
  const [newStepParam, setNewStepParam] = useState<string>('')

  const loadMacros = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await listSupportMacros()
      if (res && res.macros) {
        setMacros(res.macros)
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load macros.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    void listSupportMacros()
      .then((res) => {
        if (!cancelled && res?.macros) {
          setMacros(res.macros)
          setLoading(false)
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load macros.')
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleAddStep = () => {
    let params: Record<string, string | number | boolean | string[]> = {}
    if (newStepKind === 'apply-tag') {
      if (!newStepParam.trim()) return alert('Please enter a tag key.')
      params = { tagKey: newStepParam.trim() }
    } else if (newStepKind === 'change-priority') {
      params = { priority: newStepParam || 'P1' }
    } else if (newStepKind === 'snooze') {
      params = { durationHours: Number(newStepParam) || 24 }
    } else if (newStepKind === 'set-status') {
      params = { status: newStepParam || 'waiting-on-reporter' }
    } else if (newStepKind === 'insert-canned-reply') {
      if (!newStepParam.trim()) return alert('Please enter canned reply ID or text.')
      params = { replyId: newStepParam.trim() }
    } else if (newStepKind === 'grant-credits') {
      params = { amount: Number(newStepParam) || 50, reason: 'support_compensation' }
    }

    const nextStep: SupportMacroStep = { kind: newStepKind, params }
    setEditingMacro((prev) => ({
      ...prev,
      steps: [...(prev?.steps || []), nextStep],
    }))
    setNewStepParam('')
  }

  const handleRemoveStep = (index: number) => {
    setEditingMacro((prev) => ({
      ...prev,
      steps: (prev?.steps || []).filter((_, i) => i !== index),
    }))
  }

  const handleSave = async () => {
    if (!editingMacro?.name?.trim()) {
      alert('Macro name is required.')
      return
    }
    if (!editingMacro?.steps || editingMacro.steps.length === 0) {
      alert('At least one step is required in a macro.')
      return
    }
    setSaving(true)
    try {
      const id = editingMacro.id || `macro_${Date.now()}`
      await upsertSupportMacro({
        id,
        name: editingMacro.name.trim(),
        description: editingMacro.description?.trim() || '',
        steps: editingMacro.steps,
        defaultTags: editingMacro.defaultTags || [],
        defaultReply: editingMacro.defaultReply?.trim() || undefined,
        requiredRole: editingMacro.requiredRole || ['support', 'founder'],
      })
      setEditingMacro(null)
      await loadMacros()
    } catch (err: unknown) {
      alert(`Failed to save macro: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, color: 'var(--owner-text-primary, #f1f5f9)' }}>
            Support Macros
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--owner-text-tertiary, #64748b)' }}>
            Chained multi-step runbooks triggered with ⌘/ during support triage.
          </p>
        </div>
        <button
          type="button"
          className="ownerSupportBulkBtn"
          onClick={() =>
            setEditingMacro({
              id: '',
              name: '',
              description: '',
              steps: [],
              defaultTags: [],
            })
          }
        >
          + New macro
        </button>
      </div>

      {loading ? (
        <p style={{ color: 'var(--owner-text-secondary, #94a3b8)' }}>Loading macros…</p>
      ) : error ? (
        <p style={{ color: '#ef4444' }}>{error}</p>
      ) : macros.length === 0 ? (
        <OwnerEmptyScaffold
          title="No macros yet"
          body="Create your first macro to automate multi-step actions (credit refunds, triage, tags, status changes)."
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
          {macros.map((macro) => (
            <div
              key={macro.id}
              className="ownerSupportSidebarCard"
              style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <strong style={{ fontSize: 14, color: 'var(--owner-text-primary, #f1f5f9)' }}>
                  {macro.name}
                </strong>
                <span
                  style={{
                    fontSize: 11,
                    background: 'var(--owner-bg-hover, #1e293b)',
                    padding: '2px 6px',
                    borderRadius: 4,
                  }}
                >
                  {macro.steps.length} {macro.steps.length === 1 ? 'step' : 'steps'}
                </span>
              </div>
              {macro.description && (
                <p style={{ fontSize: 12, color: 'var(--owner-text-secondary, #94a3b8)', margin: 0 }}>
                  {macro.description}
                </p>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
                {macro.steps.map((step, idx) => (
                  <div
                    key={idx}
                    style={{
                      fontSize: 11,
                      padding: '3px 6px',
                      background: 'var(--owner-bg-page, #0f1117)',
                      borderRadius: 4,
                      border: '1px solid var(--owner-border-subtle, #1e293b)',
                    }}
                  >
                    <strong>{idx + 1}. {step.kind}</strong>
                    {step.params && Object.keys(step.params).length > 0 && (
                      <span style={{ color: 'var(--owner-text-tertiary, #64748b)', marginLeft: 6 }}>
                        ({JSON.stringify(step.params)})
                      </span>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <button
                  type="button"
                  className="ownerSupportHeaderActionBtn"
                  onClick={() => setEditingMacro(macro)}
                >
                  Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Macro Editor Modal */}
      {editingMacro && (
        <div className="ownerModalOverlay" onClick={() => setEditingMacro(null)}>
          <div
            className="ownerModalDialog"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 620, width: '100%' }}
          >
            <div className="ownerModalHeader">
              <h3>{editingMacro.id ? 'Edit Macro' : 'New Macro'}</h3>
            </div>
            <div className="ownerModalBody" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--owner-text-tertiary, #64748b)' }}>Macro Name</label>
                <input
                  type="text"
                  className="ownerSupportTagInput"
                  placeholder="e.g. Apologize and Credit 100"
                  value={editingMacro.name || ''}
                  onChange={(e) => setEditingMacro((m) => ({ ...m, name: e.target.value }))}
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ fontSize: 12, color: 'var(--owner-text-tertiary, #64748b)' }}>Description</label>
                <input
                  type="text"
                  className="ownerSupportTagInput"
                  placeholder="e.g. Grants credits, applies tag, sets resolved"
                  value={editingMacro.description || ''}
                  onChange={(e) => setEditingMacro((m) => ({ ...m, description: e.target.value }))}
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>

              {/* Step Sequence */}
              <div>
                <label style={{ fontSize: 12, color: 'var(--owner-text-tertiary, #64748b)', display: 'block', marginBottom: 6 }}>
                  Execution Steps ({editingMacro.steps?.length || 0})
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                  {(editingMacro.steps || []).map((step, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '6px 10px',
                        background: 'var(--owner-bg-surface, #141824)',
                        border: '1px solid var(--owner-border-subtle, #1e293b)',
                        borderRadius: 6,
                        fontSize: 12,
                      }}
                    >
                      <div>
                        <strong>{idx + 1}. {step.kind}</strong>
                        {step.params && (
                          <span style={{ color: 'var(--owner-text-tertiary, #64748b)', marginLeft: 6 }}>
                            {JSON.stringify(step.params)}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        className="ownerSupportTagRemoveBtn"
                        onClick={() => handleRemoveStep(idx)}
                        title="Remove step"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add Step Tool */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '160px 1fr auto',
                    gap: 8,
                    background: 'var(--owner-bg-hover, #1e293b)',
                    padding: 10,
                    borderRadius: 6,
                    alignItems: 'center',
                  }}
                >
                  <select
                    className="ownerSupportHeaderSelect"
                    value={newStepKind}
                    onChange={(e) => setNewStepKind(e.target.value as SupportMacroStep['kind'])}
                  >
                    <option value="apply-tag">apply-tag</option>
                    <option value="change-priority">change-priority</option>
                    <option value="snooze">snooze</option>
                    <option value="set-status">set-status</option>
                    <option value="grant-credits">grant-credits</option>
                    <option value="insert-canned-reply">insert-canned-reply</option>
                  </select>

                  {newStepKind === 'change-priority' ? (
                    <select
                      className="ownerSupportHeaderSelect"
                      value={newStepParam || 'P1'}
                      onChange={(e) => setNewStepParam(e.target.value)}
                    >
                      <option value="P0">P0</option>
                      <option value="P1">P1</option>
                      <option value="P2">P2</option>
                      <option value="P3">P3</option>
                    </select>
                  ) : newStepKind === 'snooze' ? (
                    <select
                      className="ownerSupportHeaderSelect"
                      value={newStepParam || '24'}
                      onChange={(e) => setNewStepParam(e.target.value)}
                    >
                      <option value="1">1 hour</option>
                      <option value="4">4 hours</option>
                      <option value="24">Tomorrow (24h)</option>
                      <option value="168">Next week (7d)</option>
                    </select>
                  ) : newStepKind === 'set-status' ? (
                    <select
                      className="ownerSupportHeaderSelect"
                      value={newStepParam || 'waiting-on-reporter'}
                      onChange={(e) => setNewStepParam(e.target.value)}
                    >
                      <option value="waiting-on-reporter">waiting-on-reporter</option>
                      <option value="in-progress">in-progress</option>
                      <option value="resolved">resolved</option>
                      <option value="dismissed">dismissed</option>
                    </select>
                  ) : (
                    <input
                      type="text"
                      className="ownerSupportTagInput"
                      placeholder={
                        newStepKind === 'apply-tag'
                          ? 'Tag name (e.g. escalated)'
                          : newStepKind === 'grant-credits'
                          ? 'Credit amount (e.g. 50)'
                          : 'Canned reply ID'
                      }
                      value={newStepParam}
                      onChange={(e) => setNewStepParam(e.target.value)}
                    />
                  )}

                  <button
                    type="button"
                    className="ownerSupportBulkBtn"
                    onClick={handleAddStep}
                  >
                    + Add
                  </button>
                </div>
              </div>
            </div>
            <div className="ownerModalActions">
              <button
                type="button"
                className="ownerModalBtn ownerModalBtn--default"
                onClick={() => setEditingMacro(null)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ownerModalBtn ownerModalBtn--primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save Macro'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
